
import './App.css';
import SubbrokerLogin from './pages/LocalSSOLogin';
import SubbrokerSignup from './pages/SubbrokerSignup';
import SsoRedirect from './pages/SsoRedirect';
import SessionUnavailable from './components/SessionUnavailable';
import { lazy, Suspense, useState, useEffect } from 'react';

import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom'
import { servicesSubbrokerSessionURL } from './utils/Urls';
import axios from 'axios'

import { Toaster } from 'react-hot-toast';
import LoaderScreen from './components/Loader';
import Shell from './components/Shell';
import { ThemeProvider } from './components/ThemeProvider';
import SupportChatWidget from './components/SupportChatWidget';

const Home = lazy(() => import('./pages/Home'))
const Account = lazy(() => import('./pages/Account'))
const Reports = lazy(() => import('./pages/Reports'))
const PledgeOrUnpledge = lazy(() => import('./pages/PledgeOrUnpledge'))
const Contacts = lazy(() => import('./pages/Contacts'))
const ContactView = lazy(() => import('./pages/ContactView'))
const Proposals = lazy(() => import('./pages/Proposals'))
const FundSearch = lazy(() => import('./pages/FundSearch'))
const FundDetail = lazy(() => import('./pages/FundDetail'))
const ProposalBasket = lazy(() => import('./pages/ProposalBasket'))
const ProposalPublicView = lazy(() => import('./pages/ProposalPublicView'))
const SipCalculator = lazy(() => import('./pages/SipCalculator'))
const Profile = lazy(() => import('./pages/Profile'))
const Payout = lazy(() => import('./pages/Payout'))

function App() {
  const [authState, setAuthState] = useState('checking')
  useEffect(() => {
    const checkUserSession = async () => {
      try {
        const response = await axios.get(servicesSubbrokerSessionURL, {
          withCredentials: true,
        })
        if (response.data.status === 'success' && response.data.user?.subbroker_code) {
          localStorage.setItem('subbroker_code', response.data.user.subbroker_code)
          setAuthState('authenticated')
          return
        }
      } catch (error) {
        localStorage.removeItem('subbroker_code')
        // A 401 means "not signed in". Anything else -- a 5xx, a timeout, the
        // network being down -- means the server could not answer, which is a
        // different thing and must not be shown as a sign-in page. Treating
        // them alike is what made #52869 read as a session bug for most of a
        // day: /auth/session was returning 500 on a missing database column and
        // the portal quietly offered the login form, so nobody saw a server
        // error anywhere.
        const status = error?.response?.status
        if (status !== 401) {
          setAuthState('unavailable')
          return
        }
      }
      setAuthState('anonymous')
    }
    checkUserSession()
  }, [])

  return (
    <div>
      <Toaster />
      <ThemeProvider>
        <Router>
          {/* Outside the Switch so it persists across route changes without
              remounting. Skips the public proposal-share page itself (a
              different audience -- an investment client, not a sub-broker --
              see SupportChatWidget's own location check). */}
          <SupportChatWidget authenticated={authState === 'authenticated'} />
          <Switch>
            {/* The root URL is the application's login entry point. */}
            <Route path="/" exact render={() => authState === 'checking' ? <LoaderScreen /> :
              authState === 'unavailable' ? <SessionUnavailable /> :
              authState === 'authenticated' ? <Shell><Suspense fallback={<LoaderScreen />}><Home /></Suspense></Shell> : <SubbrokerLogin />
            } />
            {/* Auth screens render standalone (no app shell). */}
            <Route path="/login" exact render={() => (
              authState === 'unavailable' ? <SessionUnavailable /> :
              authState === 'authenticated' ? <Redirect to="/" /> : <SubbrokerLogin />
            )} />
            <Route path="/signup" exact render={() => (
              authState === 'authenticated' ? <Redirect to="/" /> : <SubbrokerSignup />
            )} />
            <Route path="/subbroker/login" exact render={() => <Redirect to="/login" />} />
            <Route path="/subbroker/signup" exact render={() => <Redirect to="/signup" />} />
            {/* CRM's "login" link (subbroker_referral.auto_login) opens
                /redirect?token=..., which the legacy Django backend intercepts
                first (subbroker_backend/subbroker/urls.py) and forwards here
                with the same token -- this app can't own the /redirect path
                itself. Must render regardless of current auth state, since
                it's how a fresh session gets established. */}
            <Route path="/sso-redirect" exact component={SsoRedirect} />
            {/* Public, unauthenticated proposal view — the client opening a
                shared link isn't signed in, so this must render outside the
                Shell/auth gate below, same as /sso-redirect above. */}
            <Route path="/proposals/share/:token" exact render={() => (
              <Suspense fallback={<LoaderScreen />}><ProposalPublicView /></Suspense>
            )} />
            {/* Everything else lives inside the shared themed shell. */}
            <Route render={() => authState === 'checking' ? <LoaderScreen /> :
              authState !== 'authenticated' ? <Redirect to="/" /> : <Shell>
                <Suspense fallback={<LoaderScreen />}><Switch>
                  <Route path="/account" exact component={Account} />
                  <Route path="/reports" exact component={Reports} />
                  <Route path="/pledgeorunpledge" exact component={PledgeOrUnpledge} />
                  <Route path="/contacts" exact component={Contacts} />
                  <Route path="/contactview/:clientId" exact component={ContactView} />
                  <Route path="/proposals" exact component={Proposals} />
                  <Route path="/proposals/basket/:proposalId" exact component={ProposalBasket} />
                  <Route path="/mutual-funds" exact component={FundSearch} />
                  <Route path="/mutual-funds/:fundId" exact component={FundDetail} />
                  <Route path="/sip-calculator" exact component={SipCalculator} />
                  <Route path="/profile" exact component={Profile} />
                  <Route path="/payout" exact component={Payout} />
                </Switch></Suspense>
              </Shell>} />
          </Switch>
        </Router>
      </ThemeProvider>
    </div>
  );
}

export default App;
